import React, { useCallback, useEffect, useState } from 'react';
import { Card, Button, Form, FormControl, Modal, Spinner, InputGroup } from 'react-bootstrap';
import {
  FaCog,
  FaEye,
  FaEyeSlash,
  FaRegTrashAlt,
  FaUserPlus,
  FaSearch,
  FaUsers,
  FaExclamationTriangle,
} from 'react-icons/fa';
import api from '../../api/apiweb-posUser.js';
import './PosUserConfig.scss';

function PosUserConfig() {
  const [responseData, setResponseData] = useState([]);
  const [modalAddUser, setModalAddUser] = useState(false);
  const [modalEditUser, setModalEditUser] = useState(false);
  const [name, setName] = useState('');
  const [username, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [modalConfirmDelete, setModalConfirmDelete] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingList, setLoadingList] = useState(true);

  const fetchData = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await api.getAllPOSUser();
      const resp = data.data.body.sort((a, b) => {
        if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
        if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
        return 0;
      });
      setResponseData(resp);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingList(false);
    }
  }, []);

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setError(confirmPassword !== value ? 'Las contraseñas no coinciden' : null);
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    setError(password !== value ? 'Las contraseñas no coinciden' : null);
  };

  const resetForm = () => {
    setName('');
    setUserName('');
    setPassword('');
    setConfirmPassword('');
    setError(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const addPOSUser = async (event) => {
    event.preventDefault();
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    if (!(name && username && password)) return;
    setSaving(true);
    setLoading(true);
    const params = { name, username, password };
    try {
      await api.addPOSUser(params);
      setModalAddUser(false);
      fetchData();
    } catch (error) {
      console.error(error);
      setErrorMessage('Error al agregar el usuario. Inténtalo de nuevo.');
    } finally {
      setSaving(false);
      setLoading(false);
      resetForm();
    }
  };

  const handleEditUser = (user) => {
    setName(user.name);
    setUserName(user.posuser);
    setPassword(user.password);
    setConfirmPassword(user.password);
    setError(null);
    setModalEditUser(true);
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const updatePOSUser = async (event) => {
    event.preventDefault();
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    setSaving(true);
    const params = { name, username, password };
    try {
      await api.updatePOSUser(params);
      setModalEditUser(false);
      fetchData();
    } catch (error) {
      setErrorMessage('Error al actualizar el usuario. Inténtalo de nuevo.');
      console.error(error);
    } finally {
      setSaving(false);
      resetForm();
    }
  };

  const handleDeleteUser = (posuser) => {
    setUserToDelete(posuser);
    setModalConfirmDelete(true);
  };

  const confirmDeleteUser = async () => {
    setLoading(true);
    try {
      await api.deletePOSUser(userToDelete);
      fetchData();
      setModalConfirmDelete(false);
    } catch (error) {
      setErrorMessage('Error al eliminar usuario. Inténtalo de nuevo.');
      setModalConfirmDelete(false);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const showModalAddUser = () => {
    resetForm();
    setModalAddUser(true);
  };

  const filteredData = responseData.filter(
    (item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.posuser.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const passwordFields = (isEdit = false) => (
    <>
      <Form.Group className="mb-3">
        <Form.Label>{isEdit ? 'Nueva contraseña' : 'Contraseña'}</Form.Label>
        <InputGroup>
          <Form.Control
            type={showPassword ? 'text' : 'password'}
            placeholder={isEdit ? 'Deja igual para no cambiar' : 'Contraseña'}
            value={password}
            onChange={handlePasswordChange}
            required={!isEdit}
          />
          <Button variant="outline-secondary" onClick={() => setShowPassword(!showPassword)} type="button" tabIndex={-1}>
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </Button>
        </InputGroup>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Confirmar contraseña</Form.Label>
        <InputGroup>
          <Form.Control
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Confirmar contraseña"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            required={!isEdit}
            isInvalid={!!error}
          />
          <Button
            variant="outline-secondary"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            type="button"
            tabIndex={-1}
          >
            {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
          </Button>
        </InputGroup>
        {error && <div className="invalid-feedback d-block">{error}</div>}
      </Form.Group>
    </>
  );

  return (
    <div className="PosUserConfig">
      <div className="page-header page-header-row">
        <div>
          <h1 className="page-title">Usuarios POS</h1>
          <p className="page-subtitle">Administra los usuarios habilitados en cajas POS</p>
        </div>
        <Button variant="primary" className="btn-add" onClick={showModalAddUser}>
          <FaUserPlus className="me-2" />Agregar usuario
        </Button>
      </div>

      <Card className="search-card">
        <Card.Header className="search-card-header">
          <FaUsers className="me-2" />
          Usuarios
          {responseData.length > 0 && (
            <span className="results-count">{responseData.length}</span>
          )}
        </Card.Header>
        <Card.Body>
          <InputGroup className="mb-3 search-input">
            <InputGroup.Text><FaSearch /></InputGroup.Text>
            <FormControl
              type="text"
              placeholder="Buscar por nombre o usuario…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>

          {loadingList ? (
            <div className="loading-inline">
              <Spinner animation="border" variant="primary" />
              <p>Cargando usuarios…</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="empty-state">
              <FaUsers className="empty-state-icon" />
              <p className="empty-state-text">
                {responseData.length === 0
                  ? 'No hay usuarios registrados'
                  : 'No se encontraron usuarios con esa búsqueda'}
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Usuario</th>
                    <th className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((u) => (
                    <tr key={u.posuser}>
                      <td className="user-name-cell">{u.name}</td>
                      <td>{u.posuser}</td>
                      <td className="text-end">
                        <div className="row-actions">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            title="Editar usuario"
                            onClick={() => handleEditUser(u)}
                          >
                            <FaCog />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            title="Eliminar usuario"
                            onClick={() => handleDeleteUser(u.posuser)}
                          >
                            <FaRegTrashAlt />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal agregar usuario */}
      <Modal show={modalAddUser} onHide={() => setModalAddUser(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaUserPlus className="me-2 text-primary" />
            Agregar nuevo usuario
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={addPOSUser}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ingresa el nombre"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Usuario</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ingresa el usuario"
                value={username}
                onChange={(e) => setUserName(e.target.value)}
                required
              />
            </Form.Group>
            {passwordFields(false)}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setModalAddUser(false)} type="button">
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={!!error || saving}>
              {saving ? <><Spinner animation="border" size="sm" className="me-2" />Guardando…</> : 'Agregar'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal editar usuario */}
      <Modal show={modalEditUser} onHide={() => setModalEditUser(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaCog className="me-2 text-primary" />
            Editar usuario
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={updatePOSUser}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Usuario</Form.Label>
              <Form.Control
                type="text"
                value={username}
                onChange={(e) => setUserName(e.target.value)}
                required
                disabled
              />
              <Form.Text className="text-muted">El nombre de usuario no se puede modificar</Form.Text>
            </Form.Group>
            {passwordFields(true)}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setModalEditUser(false)} type="button">
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={!!error || saving}>
              {saving ? <><Spinner animation="border" size="sm" className="me-2" />Actualizando…</> : 'Actualizar'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal confirmar eliminación */}
      <Modal show={modalConfirmDelete} onHide={() => setModalConfirmDelete(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Estás seguro de eliminar al usuario <strong>{userToDelete}</strong>? Esta acción no se puede deshacer.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setModalConfirmDelete(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={confirmDeleteUser}>
            <FaRegTrashAlt className="me-2" />Eliminar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Loading */}
      <Modal show={loading} centered backdrop="static">
        <Modal.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="m-0 modal-message">Por favor, espera…</p>
        </Modal.Body>
      </Modal>

      {/* Error */}
      <Modal show={!!errorMessage} onHide={() => setErrorMessage(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaExclamationTriangle className="text-danger me-2" />
            Error
          </Modal.Title>
        </Modal.Header>
        <Modal.Body><p className="m-0">{errorMessage}</p></Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setErrorMessage(null)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default PosUserConfig;
